import { useCallback, useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";

const emptyVariant = {
  combinationKey: "DEFAULT",
  variantName: "Default",
  sku: "",
  supplierBarcode: "",
  colorId: "",
  sizeId: "",
  isActive: true,
};

const optionalText = (value) => value?.trim() || undefined;

export default function ProductVariantModal({
  productId,
  variant = null,
  onClose,
  onSaved,
}) {
  const { accessToken } = useAuth();

  const [form, setForm] = useState(emptyVariant);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEditing = Boolean(variant);

  const loadMasters = useCallback(async () => {
    try {
      const [colorsData, sizesData] = await Promise.all([
        apiRequest("/colors", { accessToken }),
        apiRequest("/sizes", { accessToken }),
      ]);

      setColors(colorsData || []);
      setSizes(sizesData || []);
    } catch (err) {
      setError(err.message || "Unable to load colors and sizes.");
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    loadMasters();

    if (variant) {
      setForm({
        combinationKey: variant.combinationKey || "DEFAULT",
        variantName: variant.variantName || "",
        sku: variant.sku || "",
        supplierBarcode: variant.supplierBarcode || "",
        colorId: variant.colorId || "",
        sizeId: variant.sizeId || "",
        isActive: variant.isActive ?? true,
      });
    }
  }, [accessToken, loadMasters, variant]);

  const updateForm = (field, value) => {
    setForm((previous) => {
      const updated = {
        ...previous,
        [field]: value,
      };

      if (field === "colorId" || field === "sizeId") {
        const color = colors.find((item) => item.id === updated.colorId);
        const size = sizes.find((item) => item.id === updated.sizeId);

        const nameParts = [color?.name, size?.name].filter(Boolean);

        updated.variantName =
          nameParts.length > 0 ? nameParts.join(" / ") : "Default";

        updated.combinationKey =
          nameParts.length > 0
            ? `${updated.colorId || "NO-COLOR"}-${updated.sizeId || "NO-SIZE"}`
            : "DEFAULT";
      }

      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.sku.trim()) {
      setError("SKU is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        combinationKey: form.combinationKey,
        variantName: optionalText(form.variantName),
        sku: form.sku.trim().toUpperCase(),
        supplierBarcode: optionalText(form.supplierBarcode),
        colorId: form.colorId || undefined,
        sizeId: form.sizeId || undefined,
        isActive: form.isActive,
      };

      if (isEditing) {
        await apiRequest(`/products/${productId}/variants/${variant.id}`, {
          method: "PATCH",
          body: payload,
          accessToken,
        });
      } else {
        await apiRequest(`/products/${productId}/variants`, {
          method: "POST",
          body: payload,
          accessToken,
        });
      }

      onSaved();
    } catch (err) {
      setError(err.message || "Unable to save variant.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold">
              {isEditing ? "Edit Variant" : "Add Variant"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              SKU, color, size, and availability. Prices are controlled by
              purchase lots.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[70vh] overflow-y-auto p-6">
            {error && (
              <div className="mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-2">
              <Select
                label="Color"
                value={form.colorId}
                onChange={(value) => updateForm("colorId", value)}
                options={colors.map((color) => [color.id, color.name])}
                placeholder="No color"
              />

              <Select
                label="Size"
                value={form.sizeId}
                onChange={(value) => updateForm("sizeId", value)}
                options={sizes.map((size) => [size.id, size.name])}
                placeholder="No size"
              />

              <Input
                label="Variant Name"
                value={form.variantName}
                onChange={(value) => updateForm("variantName", value)}
              />

              <Input
                label="SKU *"
                value={form.sku}
                onChange={(value) => updateForm("sku", value.toUpperCase())}
                placeholder="HEART-CHARM-GOLD"
              />

              <div>
                <label className="mb-2 block text-sm font-medium">
                  CHARMS barcode
                </label>

                <div className="flex h-12 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500">
                  {isEditing && variant?.barcode
                    ? variant.barcode
                    : "Automatically generated after saving"}
                </div>
              </div>

              <Input
                label="Supplier / EAN barcode"
                value={form.supplierBarcode}
                onChange={(value) => updateForm("supplierBarcode", value)}
                placeholder="Optional supplier barcode"
              />

              <Input
                label="Combination Key"
                value={form.combinationKey}
                readOnly
              />

              <label className="flex h-12 items-center gap-3 self-end rounded-xl border border-gray-200 px-4 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) =>
                    updateForm("isActive", event.target.checked)
                  }
                  className="accent-primary-700"
                />
                Variant is active
              </label>
            </div>

            <div className="mt-5 rounded-2xl border border-primary-100 bg-primary-50 p-4 text-sm leading-6 text-primary-950">
              <p className="font-semibold">Lot pricing is managed through GRNs</p>
              <p className="mt-1 text-primary-900/80">
                Cost, MRP, POS price, online price, and stock are set when a
                goods receipt is posted. This lets each purchase lot retain its
                own values.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl border border-gray-200 px-5"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="h-11 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-60"
            >
              {saving ? "Saving…" : isEditing ? "Save Variant" : "Add Variant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  readOnly = false,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <input
        type={type}
        min={type === "number" ? "0" : undefined}
        step={type === "number" ? "0.01" : undefined}
        value={value}
        readOnly={readOnly}
        onChange={(event) => onChange?.(event.target.value)}
        placeholder={placeholder}
        className={`h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 ${
          readOnly ? "bg-gray-50 text-gray-500" : ""
        }`}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      >
        <option value="">{placeholder}</option>

        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}
